import React, { useCallback, useEffect, useRef } from "react";
import Moment from "react-moment";
import moment from "moment";

import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";

import { _t, formatPrice } from "../../../functions/Functions";
import { useCustomHooks } from "../../../functions/Hooks";

const ReportPrintView = (props) => {
  const {
    isPrinting,
    setIsPrinting,
    payload,
    reportType,
    printLayout = "A4",
  } = props;

  const { t } = useTranslation();
  const { roundFormatPrice } = useCustomHooks();

  const componentRef = useRef();

  // We watch for the state to change here, and for the Promise resolve to be available
  useEffect(() => {
    if (isPrinting) {
      handlePrint();
    }
  }, [isPrinting]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { margin: 0px 10px;}`,

    onAfterPrint: () => {
      setIsPrinting(false);
    },
    onPrintError: (error) => {
      setIsPrinting(false);
    },
    onPrintCancel: () => {
      setIsPrinting(false);
    },
  });

  const getReportName = useCallback(() => {
    if (reportType === "gross_sales_report") {
      return payload?.option === "net_sales"
        ? "Net Sales Report"
        : "Gross Sales Report";
    }

    if (reportType === "foc_report") {
      return "Foc Report";
    }

    if (reportType === "sales_tender_report") {
      return "Sales Tender Report";
    }

    if (reportType === "sales_sync_report") {
      return payload?.reportTaxType?.name + " Tax Report";
    }

    if (reportType === "food_panda_report") {
      return "Food Panda Report";
    }
    if (reportType === "call_center_report") {
      return "Call Center Report";
    }

    return "";
  }, [reportType, payload]);

  if (!payload?.reportData) return null;

  const renderReportContent = () => {
    switch (reportType) {
      // -- SALES TENDER --
      case "sales_tender_report": {
        const columns = [
          { key: "group_name", header: "Group Name" },
          { key: "food_item", header: "Item" },
          { key: "DineIn", header: "Dine In" },
          { key: "Delivery", header: "Delivery" },
          { key: "Takeaway", header: "Takeaway" },
          { key: "FoodPanda", header: "Food Panda" },
          { key: "Wasted", header: "Waste" },
          { key: "Refund", header: "Return" },
          { key: "Total", header: "Total" },
        ];

        // Header
        const renderTableHeader = () => (
          <thead className="align-middle">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="sm-text text-capitalize align-middle text-center border-1 border"
                  style={{ minWidth: "40px", padding: "5px" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        );

        // Body
        const renderGroups = () => {
          if (!payload?.reportData) return null;

          return Object.keys(payload.reportData).map((group, groupIdx) => {
            // Group Header
            const groupRow = (
              <tr key={`group-row-${group}-${groupIdx}`}>
                <td className="sm-text text-capitalize align-middle text-center border">
                  {group}
                </td>
                {[...Array(columns.length - 1)].map((_, i) => (
                  <td
                    key={i}
                    className="sm-text text-capitalize align-middle text-center border"
                  ></td>
                ))}
              </tr>
            );
            // Items
            const itemRows = payload.reportData[group].map((item, i) => {
              // If keys change, adjust mapping order!
              const total =
                parseFloat(item.DineIn) +
                parseFloat(item.Delivery) +
                parseFloat(item.Takeaway) +
                parseFloat(item.FoodPanda) -
                parseFloat(item.Wasted) -
                parseFloat(item.Refund);
              return (
                <tr key={`item-row-${i}`}>
                  <td className="sm-text text-capitalize align-middle text-center border"></td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.food_item}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.DineIn}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.Delivery}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.Takeaway}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.FoodPanda}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.Wasted}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {item.Refund}
                  </td>
                  <td className="sm-text text-capitalize align-middle text-center border">
                    {total?.toFixed(2)}
                  </td>
                </tr>
              );
            });
            return (
              <tbody className="align-middle" key={`tbody-${groupIdx}`}>
                {groupRow}
                {itemRows}
              </tbody>
            );
          });
        };

        return (
          <div className="col-12">
            <table className="table table-bordered-sm table-hover">
              {renderTableHeader()}
              {renderGroups()}
            </table>
          </div>
        );
      }

      // -- FOC REPORT --
      case "foc_report": {
        // Columns (using call_center_report approach for structure)
        const columns = [
          { key: "order_date", header: _t(t("Order Date")) },
          {
            key: "token_no",
            header: _t(t("Token No")),
            render: (item) => item.token.id,
          },
          { key: "branch_name", header: _t(t("Branch Name")) },
          { key: "order_type_name", header: _t(t("Order Type")) },
          { key: "party_name", header: _t(t("Party Name")) },
          { key: "customer_name", header: _t(t("Customer Name")) },
          {
            key: "delivery_phn_no",
            header: _t(t("Customer Phone No")),
            render: (item) => item.delivery_phn_no ?? "-",
          },
          { key: "order_bill", header: _t(t("Order Bill")) },
          { key: "discount_amount", header: _t(t("Discount")) },
          { key: "vat", header: _t(t("Vat")) },
          { key: "delivery_charges", header: _t(t("Delivery Charges")) },
          { key: "total_payable", header: _t(t("Total")) },
          { key: "user_name", header: _t(t("User Name")) },
          {
            key: "status",
            header: _t(t("Status")),
            render: (item) => {
              const is_cancelled = parseInt(item.is_cancelled);
              const is_ready = parseInt(item.is_ready);
              const is_refund = parseInt(item.is_refund);
              const is_foc = parseInt(item.is_foc);
              if (is_cancelled === 0) {
                if (is_ready === 1 && is_refund === 0 && is_foc === 0)
                  return <span>{_t(t("Ready"))}</span>;
                if (is_refund === 1 && is_foc === 0)
                  return <span>{_t(t("Refund"))}</span>;
                if (is_foc === 1) return <span>{_t(t("Free of cost"))}</span>;
                return <span>{_t(t("processing"))}</span>;
              }
              return <span>{_t(t("Cancelled"))}</span>;
            },
          },
          {
            key: "reason",
            header: _t(t("Reason")),
            render: (item) => item.reason ?? "-",
          },
          {
            key: "order_source",
            header: _t(t("Order Source")),
            render: (item) => item.order_source ?? "-",
          },
        ];
        // For table header (without translation as in the original)
        const plainHeaders = [
          "Order Date",
          "Token No",
          "Branch Name",
          "Order Type",
          "Party Name",
          "Customer Name",
          "Customer Phone No",
          "Order Bill",
          "Discount",
          "Vat",
          "Delivery Charges",
          "Total",
          "User Name",
          "Status",
          "Reason",
          "Order Source",
        ];

        // Totals
        let grandTotals = {
          total_price: 0,
          grand_total: 0,
          tax_total: 0,
          g_tax: 0,
          g_discount: 0,
          g_delCharges: 0,
          g_afterDiscount: 0,
        };

        // Renders group rows like call_center_report
        const renderGroups = () =>
          Object.keys(payload?.reportData || {}).map((group_name, groupIdx) => {
            let sum = 0,
              g_total = 0,
              discount = 0,
              delCharges = 0,
              afterDiscount = 0,
              tax_total = 0;
            const groupRows = payload.reportData[group_name].map((item, i) => {
              sum += parseFloat(item.order_bill || 0);
              grandTotals.total_price += parseFloat(item.order_bill || 0);
              g_total += parseFloat(item.total_payable || 0);
              grandTotals.grand_total += parseFloat(item.total_payable || 0);
              tax_total += parseFloat(item.vat || 0);
              grandTotals.g_tax += parseFloat(item.vat || 0);
              discount += parseFloat(item.discount_amount || 0);
              grandTotals.g_discount += parseFloat(item.discount_amount || 0);
              delCharges += parseFloat(item.delivery_charges || 0);
              grandTotals.g_delCharges += parseFloat(
                item.delivery_charges || 0
              );
              afterDiscount += parseFloat(item.gross_after_discount || 0);
              grandTotals.g_afterDiscount += parseFloat(
                item.gross_after_discount || 0
              );
              grandTotals.g_total_guest += parseFloat(item.total_guest || 0);
              return (
                <tr key={i}>
                  {columns.map((col, ci) => (
                    <td key={col.key + ci} className="align-middle">
                      {"render" in col ? col.render(item) : item[col.key]}
                    </td>
                  ))}
                </tr>
              );
            });
            return (
              <React.Fragment key={groupIdx}>
                <tr>
                  <th
                    scope="row"
                    className="border border-1 table_report_col"
                    colSpan={plainHeaders.length}
                  >
                    {group_name}
                  </th>
                </tr>
                {groupRows}
                <tr>
                  <th colSpan="6"></th>
                  <th>Total</th>
                  <td>{roundFormatPrice(sum)}</td>
                  <td>{discount}</td>
                  <td>{roundFormatPrice(tax_total)}</td>
                  <td>{delCharges}</td>
                  <td>{roundFormatPrice(g_total)}</td>
                </tr>
              </React.Fragment>
            );
          });

        const shouldShow =
          payload?.reportData && payload?.generatedReport === true;

        return (
          <div className="col-12">
            {shouldShow && (
              <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
                <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                  <table className="table table-bordered-sm table-hover">
                    <thead className="align-middle">
                      <tr>
                        {plainHeaders.map((h, i) => (
                          <th key={i} scope="col">
                            {_t(t(h))}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="align-middle">
                      {renderGroups()}
                      <tr>
                        <th colSpan="6"></th>
                        <th>Grand Total</th>
                        <td>{roundFormatPrice(grandTotals.total_price)}</td>
                        <td>{grandTotals.g_discount}</td>
                        <td>{roundFormatPrice(grandTotals.g_tax)}</td>
                        <td>{grandTotals.g_delCharges}</td>
                        <td>{roundFormatPrice(grandTotals.grand_total)}</td>
                      </tr>
                      <tr>
                        <th colSpan="7"></th>
                        <td>Gross Amount</td>
                        <td>Discount</td>
                        <td>Vat</td>
                        <td>D.C</td>
                        <td>Total</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      }

      // -- GROSS SALES --
      case "gross_sales_report": {
        // Define columns from reportData or use fallback
        const getDataColumnKeys = () => {
          if (Array.isArray(payload?.reportData) && payload.reportData.length)
            return Object.keys(payload.reportData[0]);
          if (
            payload?.reportData &&
            typeof payload.reportData === "object" &&
            Object.keys(payload.reportData).length > 0
          ) {
            const group =
              payload.reportData[Object.keys(payload.reportData)[0]];
            if (Array.isArray(group) && group.length)
              return Object.keys(group[0]);
          }
          return [];
        };

        const colKeys = getDataColumnKeys();

        // DRY header
        const renderTableHeader = () => (
          <thead className="align-middle">
            <tr>
              {!Array.isArray(payload?.reportData) && (
                <th>{_t(t("Branch"))}</th>
              )}
              {colKeys.map((colKey) => (
                <th key={colKey} scope="col">
                  {_t(t(colKey))}
                </th>
              ))}
            </tr>
          </thead>
        );

        // DRY rows
        const renderTableRows = (rows, colSpan = 0) =>
          rows.map((row, idx) => (
            <tr key={idx}>
              {colSpan > 0 && <td colSpan={colSpan}></td>}
              {colKeys.map((colKey) => {
                let val = row[colKey];
                if (colKey === "Date") val = moment(val).format("DD-MMM-YYYY");
                if (colKey === "Month") val = moment(val).format("MMM-YYYY");
                return <td key={colKey}>{val}</td>;
              })}
            </tr>
          ));

        // Main render grouped/flat
        const renderBody = () => {
          if (Array.isArray(payload?.reportData))
            return renderTableRows(payload.reportData);
          if (
            payload?.reportData &&
            typeof payload.reportData === "object" &&
            Object.keys(payload.reportData).length
          ) {
            return Object.entries(payload.reportData).map(
              ([groupKey, rows]) => (
                <React.Fragment key={groupKey}>
                  <tr>
                    <th>{groupKey}</th>
                  </tr>
                  {renderTableRows(rows, 1)}
                </React.Fragment>
              )
            );
          }
          return null;
        };

        if (
          payload.reportData === null ||
          payload.reportData === undefined ||
          payload.generatedReport !== true
        )
          return null;

        return (
          <div className="col-12">
            <div className="row gx-2 justify-content-center">
              <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                <table className="table table-bordered table-hover min-table-height mt-3">
                  {renderTableHeader()}
                  <tbody className="align-middle">{renderBody()}</tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }

      // -- SALES SYNC REPORT --
      case "sales_sync_report": {
        const columns = [
          { key: "branch_name", header: _t(t("Branch")) },
          {
            key: "token_no",
            header: _t(t("Token No")),
            className: "table_child_token",
          },
          {
            key: "table_name",
            header: _t(t("Table Name")),
            className: "table_child_total",
          },
          {
            key: "invoice_no",
            header: _t(t("Invoice No")),
            className: "table_child_token",
            render: (item) => `'${item.invoice_no}`,
          },
          {
            key: "order_date",
            header: _t(t("Invoice Date")),
            className: "table_child_token",
          },
          {
            key: "order_type_name",
            header: _t(t("Customer Type")),
            className: "table_child_token",
          },
          {
            key: "payment_type",
            header: _t(t("Payment Type")),
            className: "table_child_total",
          },
          {
            key: "order_bill",
            header: _t(t("Gross Amount")),
            className: "table_report_col",
          },
          {
            key: "discount_amount",
            header: _t(t("Discount")),
            className: "table_report_col",
          },
          {
            key: "vat",
            header: _t(t("Tax Amount")),
            className: "table_report_col",
          },
          {
            key: "service_charges",
            header: _t(t("Service Charges")),
            className: "table_report_col",
          },
          {
            key: "delivery_charges",
            header: _t(t("Delivery Charges")),
            className: "table_report_col",
          },
          {
            key: "total_payable",
            header: _t(t("Net Amount")),
            className: "table_report_col",
          },
        ];

        let grossGrandTotal = 0,
          discountGrandTotal = 0,
          taxGrandTotal = 0,
          netGrandTotal = 0;

        const renderGroups = () => {
          if (!payload.reportData || typeof payload.reportData !== "object")
            return null;
          return Object.entries(payload.reportData).map(
            ([branchName, orders]) => {
              let groupGross = 0,
                groupDiscount = 0,
                groupTax = 0,
                groupNet = 0;
              const rows =
                Array.isArray(orders) &&
                orders.map((item, idx) => {
                  const gross = parseFloat(item.order_bill) || 0;
                  const discount = parseFloat(item.discount_amount) || 0;
                  const tax = parseFloat(item.vat) || 0;
                  const net = parseFloat(item.total_payable) || 0;
                  groupGross += gross;
                  grossGrandTotal += gross;
                  groupDiscount += discount;
                  discountGrandTotal += discount;
                  groupTax += tax;
                  taxGrandTotal += tax;
                  groupNet += net;
                  netGrandTotal += net;
                  return (
                    <tr key={idx}>
                      {columns.map((col, i) => (
                        <td
                          key={col.key + i}
                          className={`sm-text align-middle${
                            col.className ? " " + col.className : ""
                          }`}
                        >
                          {"render" in col ? col.render(item) : item[col.key]}
                        </td>
                      ))}
                    </tr>
                  );
                });
              return (
                <React.Fragment key={branchName}>
                  <tr>
                    <td colSpan={columns.length}>{branchName}</td>
                  </tr>
                  {rows}
                  <tr>
                    <th colSpan={6}></th>
                    <th>Total</th>
                    <th>{groupGross}</th>
                    <th>{groupDiscount}</th>
                    <th>{groupTax}</th>
                    <th></th>
                    <th></th>
                    <th>{groupNet}</th>
                  </tr>
                </React.Fragment>
              );
            }
          );
        };

        // Table header
        const renderTableHeader = () => (
          <thead className="align-middle">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.key + i}
                  scope="col"
                  className={`sm-text align-middle${
                    col.className ? " " + col.className : ""
                  }`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        );

        if (
          payload.reportData !== null &&
          payload.reportData !== undefined &&
          payload.generatedReport === true
        ) {
          return (
            <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
              <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                <table className="table table-bordered table-hover min-table-height mt-3">
                  {renderTableHeader()}
                  <tbody className="align-middle">
                    {renderGroups()}
                    <tr className="sm-text">
                      <th colSpan={6}></th>
                      <th>Grand Total</th>
                      <th>{grossGrandTotal}</th>
                      <th>{discountGrandTotal}</th>
                      <th>{taxGrandTotal}</th>
                      <th></th>
                      <th></th>
                      <th>{netGrandTotal}</th>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        return null;
      }

      // -- FOOD PANDA REPORT --
      case "food_panda_report": {
        const columns = [
          { key: "branch_name", header: _t(t("Branch")) },
          {
            key: "order_bill",
            header: _t(t("Invoice Amount - Including Tax")),
          },
          {
            key: "exclusive_tax",
            header: _t(t("Invoice Amount - Excluding Tax")),
          },
          { key: "scheme_discount", header: _t(t("Scheme Discount ")) },
          { key: "claimable_amount", header: _t(t("Claimable Discount")) },
          { key: "unclaimable_amount", header: _t(t("Unclaimable Discount")) },
          { key: "total_payable", header: _t(t("Total Payable")) },
        ];
        const totals = {
          order_bill: 0,
          exclusive_tax: 0,
          scheme_discount: 0,
          claimable_amount: 0,
          unclaimable_amount: 0,
          total_payable: 0,
        };
        const reportData = payload?.reportData || [];
        reportData.forEach((item) => {
          totals.order_bill += parseFloat(item.order_bill) || 0;
          totals.exclusive_tax += parseFloat(item.exclusive_tax) || 0;
          totals.scheme_discount += parseFloat(item.scheme_discount) || 0;
          totals.claimable_amount += parseFloat(item.claimable_amount) || 0;
          totals.unclaimable_amount += parseFloat(item.unclaimable_amount) || 0;
          totals.total_payable += parseFloat(item.total_payable) || 0;
        });
        // Table header
        const renderTableHeader = () => (
          <thead className="align-middle">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="table_report_col sm-text text-capitalize align-middle text-center border-1 border"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        );
        // Table body
        const renderTableBody = () => (
          <tbody className="align-middle">
            {reportData.map((item, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    scope="row"
                    className="xsm-text text-capitalize align-middle text-center"
                  >
                    {col.key === "branch_name"
                      ? item.branch_name
                      : formatPrice(item[col.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <th className="sm-text text-capitalize align-middle text-center border-1 border">
                Total
              </th>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.order_bill)}
              </td>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.exclusive_tax)}
              </td>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.scheme_discount)}
              </td>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.claimable_amount)}
              </td>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.unclaimable_amount)}
              </td>
              <td className="sm-text text-capitalize align-middle text-center border-1 border">
                {formatPrice(totals.total_payable)}
              </td>
            </tr>
          </tbody>
        );
        if (
          payload?.reportData !== null &&
          payload?.reportData !== undefined &&
          payload?.generatedReport === true
        ) {
          return (
            <div className="row gx-2 justify-content-center t-pt-15 report">
              <div className="col-12  mb-md-0 table-responsive">
                <table className="table table-bordered table-hover min-table-height mt-3">
                  {renderTableHeader()}
                  {renderTableBody()}
                </table>
              </div>
            </div>
          );
        }
        return null;
      }

      // -- CALL CENTER --
      case "call_center_report": {
        const columns = [
          {
            key: "created_at",
            header: _t(t("Date")),
            render: (item) => <Moment format="LL">{item.created_at}</Moment>,
          },
          {
            key: "token",
            header: _t(t("Token")),
            render: (item) => item.token.id,
          },
          { key: "branch_name", header: _t(t("Branch Name")) },
          { key: "order_type_name", header: _t(t("Order Type")) },
          { key: "party_name", header: _t(t("Party Name")) },
          { key: "customer_name", header: _t(t("Customer Name")) },
          { key: "delivery_phn_no", header: _t(t("Phone No")) },
          {
            key: "payment_type",
            header: _t(t("Payment Type")),
            render: (item) =>
              item.bill_distribution !== null
                ? item.bill_distribution[0].payment_type
                : "",
          },
          { key: "order_bill", header: _t(t("Order Bill")) },
          { key: "discount_amount", header: _t(t("Discount")) },
          { key: "vat", header: _t(t("Vat")) },
          { key: "total_payable", header: _t(t("Total")) },
          { key: "user_name", header: _t(t("User Name")) },
          {
            key: "status",
            header: _t(t("Status")),
            render: (item) => {
              const is_cancelled = parseInt(item.is_cancelled);
              const is_ready = parseInt(item.is_ready);
              const is_refund = parseInt(item.is_refund);
              const is_foc = parseInt(item.is_foc);

              if (is_cancelled === 0) {
                if (is_ready === 1 && is_refund === 0 && is_foc === 0)
                  return <>{_t(t("Ready"))}</>;
                else if (is_refund === 1 && is_foc === 0)
                  return <>{_t(t("Refund"))}</>;
                else if (is_foc === 1) return <>{_t(t("Free of cost"))}</>;
                else return <>{_t(t("processing"))}</>;
              }
              return <>{_t(t("Cancelled"))}</>;
            },
          },
          {
            key: "reason",
            header: _t(t("Reason")),
            render: (item) => (item.reason ? item.reason : "-"),
          },
        ];

        let reportTotals = {
          total_price: 0,
          grand_total: 0,
          g_tax: 0,
          g_discount: 0,
          g_delCharges: 0,
          g_serCharges: 0,
        };

        const renderGroups = () => {
          return Object.keys(payload?.reportData || {}).map(
            (group_name, groupIdx) => {
              let sum = 0;
              let g_total = 0;
              let discount = 0;
              let delCharges = 0;
              let serCharges = 0;
              let tax_total = 0;
              const groupRows = payload.reportData[group_name].map(
                (item, i) => {
                  const order_bill = parseFloat(item.order_bill) || 0;
                  const total_payable = parseFloat(item.total_payable) || 0;
                  const vat = parseFloat(item.vat) || 0;
                  const discount_amount = parseFloat(item.discount_amount) || 0;
                  const delivery_charges =
                    parseFloat(item.delivery_charges) || 0;
                  const service_amount = item.service_amount
                    ? parseFloat(item.service_amount)
                    : 0;

                  sum += order_bill;
                  reportTotals.total_price += order_bill;

                  g_total += total_payable;
                  reportTotals.grand_total += total_payable;

                  tax_total += vat;
                  reportTotals.g_tax += vat;

                  discount += discount_amount;
                  reportTotals.g_discount += discount_amount;

                  delCharges += delivery_charges;
                  reportTotals.g_delCharges += delivery_charges;

                  serCharges += service_amount;
                  reportTotals.g_serCharges += service_amount;

                  return (
                    <tr key={i}>
                      {columns.map((col, ci) => {
                        if (ci === 0) return <td key={ci}></td>;
                        const val = col.render
                          ? col.render(item, i)
                          : item[col.key];
                        return (
                          <td
                            key={ci}
                            className="xsm-text text-capitalize align-middle text-center"
                          >
                            {val}
                          </td>
                        );
                      })}
                    </tr>
                  );
                }
              );

              // Group footer (Total)
              return (
                <React.Fragment key={groupIdx}>
                  <tr>
                    <th
                      scope="row"
                      className="md-text text-capitalize align-middle text-center"
                    >
                      {group_name}
                    </th>
                  </tr>
                  {groupRows}
                  <tr className="sm-text">
                    <th colSpan={6}></th>
                    <th className=" text-capitalize align-middle text-center border-1 border">
                      Total
                    </th>
                    <td className="sm-text text-capitalize align-middle text-center border-1 border">
                      {formatPrice(sum)}
                    </td>
                    <td className=" align-middle text-center">{discount}</td>
                    <td></td>
                    <td></td>
                    <td className="sm-text text-capitalize align-middle text-center border-1 border">
                      {formatPrice(tax_total)}
                    </td>
                    <td className=" align-middle text-center">{delCharges}</td>
                    <td className=" align-middle text-center">{serCharges}</td>
                    <td className="sm-text text-capitalize align-middle text-center border-1 border">
                      {formatPrice(g_total)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            }
          );
        };

        const renderGrandTotalRow = () => (
          <tr className="sm-text text-capitalize align-middle text-center border-1 border">
            <th colSpan={6}></th>
            <th>Grand Total</th>
            <td className="xsm-text align-middle text-center">
              {formatPrice(reportTotals.total_price)}
            </td>
            <td>{reportTotals.g_discount}</td>
            <td></td>
            <td></td>
            <td>{formatPrice(reportTotals.g_tax)}</td>
            <td>{reportTotals.g_delCharges}</td>
            <td>{reportTotals.g_serCharges}</td>
            <td className="xsm-text align-middle text-center">
              {formatPrice(reportTotals.grand_total)}
            </td>
          </tr>
        );

        const renderFooterHeaderRow = () => (
          <tr>
            <th colSpan={6}></th>
            <th className="sm-text text-capitalize align-middle text-center border-1 border"></th>
            <td className="sm-text align-middle text-center">Gross Amount</td>
            <td className="sm-text align-middle text-center">Discount</td>
            <td></td>
            <td></td>
            <td className="sm-text align-middle text-center">Vat</td>
            <td className="sm-text align-middle text-center">D.C</td>
            <td className="sm-text align-middle text-center">S.C</td>
            <td className="sm-text align-middle text-center">Total</td>
          </tr>
        );

        const renderTableHeader = () => (
          <thead className="align-middle">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  scope="col"
                  className="sm-text table_report_col text-capitalize align-middle text-center border-1 border"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        );

        return (
          <>
            {payload?.reportData && payload?.generatedReport && (
              <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
                <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                  <table className="table table-bordered table-hover min-table-height mt-3">
                    {renderTableHeader()}
                    <tbody className="align-middle">
                      {renderGroups()}
                      {renderGrandTotalRow()}
                      {renderFooterHeaderRow()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      }

      default:
        return <h5 className="text-center">No report found.</h5>;
    }
  };

  return (
    <div className="d-none">
      <div ref={componentRef}>
        <div className="fk-print container-fluid">
          <div className="text-center mb-3">
            <h5 className="font-weight-bold text-uppercase">
              {payload?.siteName}
            </h5>
            {payload?.isShowBranch ? (
              <h6 className="font-weight-bold text-uppercase">
                {payload?.branch?.name || "All Branches"}
              </h6>
            ) : null}
            <div>
              <span className="mt-1 d-block fk-print-text_pos font-weight-bold text-uppercase text-center table-text">
                FROM&nbsp;
                <Moment format="LL">{payload?.startDate}</Moment>
                &nbsp;TO&nbsp;
                <Moment format="LL">{payload?.endDate}</Moment>
              </span>
            </div>
            <div>
              <span className="mt-1 d-block fk-print-text_pos font-weight-bold text-uppercase text-center table-text">
                Print Time: <Moment format="LLL">{new Date()}</Moment>
              </span>
            </div>
            <span className="mt-1 d-block fk-print-text_pos font-weight-bold text-uppercase text-center table-text">
              {getReportName()}
            </span>
          </div>
          {renderReportContent()}
          <h5 className="mt-3 text-center">Powered by indolj.pk</h5>
        </div>
      </div>
    </div>
  );
};

export default ReportPrintView;
